import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import classnames from 'classnames';
import { kebabCase } from 'lodash';
import isMobileDevice from 'utils/isMobileDevice';
import { withStyles } from 'theme/withStyles';
import Amount from 'fields/Amount';
import AmountSelector from 'fields/AmountSelector';
import AmountSlider from 'fields/AmountSlider';
import AutoComplete from 'fields/AutoComplete';
import AtomicFi from 'fields/AtomicFi';
import BooleanField from 'fields/Boolean';
import BooleanSelector from 'fields/BooleanSelector';
import Button from 'fields/Button';
import Decimal from 'fields/Decimal';
import Disclosure from 'fields/Disclosure';
import FundingAccountSelector from 'fields/FundingAccountSelector';
import FundingTypeSelector from 'fields/FundingTypeSelector';
import Html from 'fields/Html';
import Lookup from 'fields/Lookup';
import LookupCardNetwork from 'fields/LookupCardNetwork';
import LookupHorizontalVertical from 'fields/LookupHorizontalVertical';
import LookupSelector from 'fields/LookupSelector';
import MaskedSecretString from 'fields/MaskedSecretString';
import MaskedSecretVariableString from 'fields/MaskedSecretVariableString';
import MultiChoice from 'fields/MultiChoice';
import MultiDisclosure from 'fields/MultiDisclosure';
import MultiSelect from 'fields/MultiSelect';
import NumberField from 'fields/Number';
import Output from 'fields/Output';
import OutputFunding from 'fields/OutputFunding';
import PhoneNumber from 'fields/PhoneNumber';
import StringField from 'fields/String';
import Toggle from 'fields/Toggle';
import Grid from 'fields/Grid';
import OutputRates from 'fields/OutputRates';
import AmountMobileTheme from 'fields/AmountMobileTheme';
import Verification from 'fields/Verification';
import SocureProgress from 'fields/SocureProgress';
import RetrySocureFlow from 'fields/SocureProgress/RetrySocureFlow';

const styles = ({ palette, screenSizes, spacing }) => ({
  root: ({ fieldType }) => {
    if (!fieldType) {
      return {};
    }
    switch (fieldType) {
      case 'AMOUNT_SELECTOR':
        return {
          borderBottom: 'none',
          display: 'block',
          width: '100%',
        };

      default:
        return {
          '& .spinner': {
            background: palette.background,
            display: ({ loading }) => (loading ? 'block' : 'none'),
            position: 'absolute',
            right: spacing.unit * 2,
            top: 9,
            zIndex: 5,
          },
          '&.col-sm-12': {
            width: '100%',
          },
          '&.col-sm-3': {
            width: 'calc(25% - 10px)',
            [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
              width: '100%',
            },
          },
          '&.col-sm-6': {
            width: 'calc(50% - 10px)',
            [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
              width: '100%',
            },
          },
          '&.col-sm-9': {
            width: 'calc(75% - 10px)',
            [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
              width: '100%',
            },
          },
          position: 'relative',
        };
    }
  },
});

const getColumnSizeClass = (colSize) => {
  switch (colSize) {
    case 1:
      return 'col-sm-3';
    case 2:
      return 'col-sm-6';
    case 3:
      return 'col-sm-9';
    default:
      return 'col-sm-12';
  }
};

const getFieldTypeClass = (fieldType) => `field-type--${kebabCase(fieldType).toLowerCase()}`;

const getRequiredClass = (required) => (required ? 'required' : 'optional');

const getTouchedClass = (type, hasValue, focus) => {
  switch (type.toLowerCase()) {
    case 'amount_slider':
      return hasValue ? 'touched' : '';
    case 'multi_select':
      return hasValue || focus ? 'touched' : '';
    default:
      return '';
  }
};

const Field = ({
  classes,
  columnSize,
  fieldName,
  fieldType,
  groupName,
  hasLayout,
  hasNoFormat,
  hasReRender,
  hasValue,
  hidden,
  isFocus,
  listGroupName,
  required,
  shouldValidate,
}) => {
  const fieldProps = {
    groupName,
    listGroupName,
    name: fieldName,
    shouldValidate,
  };

  const allFields = useSelector(({ fields }) => fields);
  const isMobileAndUsesTheme = useMemo(() => !!classes.root && isMobileDevice(), [classes.root]);

  const fieldHavingMobilePhoneField = Object.values(allFields)?.find(
    (currentField) => currentField.mobilePhoneField?.length > 0,
  );

  if (hidden) {
    return null;
  }
  if (fieldType === undefined) {
    // eslint-disable-next-line no-console
    console.error(`MISCONFIGURATION: field "${fieldName}" does not exist!`);
    return null;
  }

  if (fieldHavingMobilePhoneField?.mobilePhoneField === fieldName) {
    return null;
  }
  return (
    <div
      className={classnames(
        getColumnSizeClass(columnSize),
        getFieldTypeClass(fieldType),
        getRequiredClass(required),
        getTouchedClass(fieldType, hasValue, isFocus),
        classes.root,
      )}
      data-name={fieldName}
      data-rerender={hasReRender ? '' : undefined}
    >
      {(() => {
        /* TODO: Remove this once the backend is fixed */
        /** istanbul ignore if */
        if (fieldName === 'appRequest_totalFundingAmount') {
          return <OutputFunding {...fieldProps} />;
        }
        /** istanbul ignore switch */
        switch (fieldType.toLowerCase()) {
          case 'amount':
            return <Amount {...fieldProps} />;
          case 'amount_selector':
            if (isMobileAndUsesTheme) {
              return <AmountMobileTheme {...fieldProps} />;
            }
            return <AmountSelector {...fieldProps} />;
          case 'amount_slider':
            return <AmountSlider {...fieldProps} />;
          case 'auto_complete':
            return <AutoComplete {...fieldProps} />;
          case 'atomicfi':
            return <AtomicFi {...fieldProps} />;
          case 'boolean':
            return <BooleanField {...fieldProps} />;
          case 'boolean_selector':
            return <BooleanSelector {...fieldProps} />;
          case 'button':
            return <Button {...fieldProps} />;
          case 'date':
          case 'local_date': {
            return <StringField {...fieldProps} />;
          }
          case 'decimal':
            return <Decimal {...fieldProps} />;
          case 'disclosure':
            return <Disclosure {...fieldProps} />;
          case 'funding_account_selector':
            return <FundingAccountSelector {...fieldProps} />;
          case 'funding_type_selector':
            return <FundingTypeSelector {...fieldProps} />;
          case 'lookup_composite':
          case 'lookup': {
            if (hasLayout) {
              return <LookupHorizontalVertical {...fieldProps} />;
            }
            return <Lookup {...fieldProps} />;
          }
          case 'lookup_card_network':
            return <LookupCardNetwork />;
          case 'lookup_selector':
            return <LookupSelector {...fieldProps} />;
          case 'masked_string':
            return <StringField {...fieldProps} />;
          case 'masked_secret_variable_string':
            if (hasNoFormat) {
              return <StringField {...fieldProps} />;
            }
            return <MaskedSecretVariableString {...fieldProps} />;
          case 'masked_secret_string':
            if (hasNoFormat) {
              return <StringField {...fieldProps} />;
            }
            return <MaskedSecretString {...fieldProps} />;
          case 'multi_choice':
            return <MultiChoice {...fieldProps} />;
          case 'multi_disclosures':
            return <MultiDisclosure {...fieldProps} />;
          case 'multi_select':
            return <MultiSelect {...fieldProps} />;
          case 'number':
            return <NumberField {...fieldProps} />;
          case 'output':
            return <Output {...fieldProps} />;
          case 'phone_number':
            return <PhoneNumber {...fieldProps} />;
          case 'recaptcha':
            /** hack the backend shouldn't return this field */
            return null;
          case 'html_element':
            return <Html {...fieldProps} />;
          case 'grid':
            return <Grid {...fieldProps} />;
          case 'output_rates':
            return <OutputRates {...fieldProps} />;
          case 'verification_type':
            return <Verification {...fieldProps} />;
          case 'socure_progress':
            return <SocureProgress {...fieldProps} />;
          case 'retry_verification_type':
            return <RetrySocureFlow {...fieldProps} />;
          case 'google_places_autocomplete':
          case 'password':
          case 'month_year':
          case 'string':
          default:
            if (fieldName === 'appRequest_fundingOption') {
              return <Toggle {...fieldProps} />;
            }
            return <StringField {...fieldProps} />;
        }
      })()}
    </div>
  );
};

Field.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  columnSize: PropTypes.number,
  fieldName: PropTypes.string.isRequired,
  fieldType: PropTypes.string,
  groupName: PropTypes.string.isRequired,
  hasLayout: PropTypes.bool.isRequired,
  hasNoFormat: PropTypes.bool.isRequired,
  hasReRender: PropTypes.bool.isRequired,
  hasValue: PropTypes.bool.isRequired,
  hidden: PropTypes.bool,
  isFocus: PropTypes.bool.isRequired,
  listGroupName: PropTypes.string,
  required: PropTypes.bool,
  shouldValidate: PropTypes.bool,
};

Field.defaultProps = {
  columnSize: 4,
  fieldType: undefined,
  hidden: false, // required for kba page since they don't return fields
  listGroupName: undefined,
  required: undefined,
  shouldValidate: true,
};

const mapStateToProps = ({ data, fields, ui }, { fieldName, groupName, listGroupName }) => {
  const field = fields[fieldName] || {};
  const [listGroupFieldName, listIndex] = fieldName?.split(':') || '';
  return {
    columnSize: field.columnSize,
    fieldType: field.fieldType,
    hasLayout: !!field.layout,
    hasNoFormat: !field.format,
    hasReRender: !!field.reRender,
    hasValue: !!(listGroupName
      ? data[listGroupName][parseInt(listIndex, 10)][listGroupFieldName]
      : data[fieldName]),
    hidden:
      (groupName === 'LOOKUP_COMPOSITE' && field.conditional) || groupName !== 'LOOKUP_COMPOSITE'
        ? field.hidden
        : false,
    isFocus: ui.fieldFocus === fieldName,
    required: field.required,
  };
};

const FieldStyled = withStyles(styles)(Field);

export { FieldStyled as BaseField };

export default connect(mapStateToProps)(FieldStyled);
